import React, { useEffect, useRef, useState } from "react";
import NewestJobs from "./NewestJobs";
import { useAuth } from "../../hooks/useAuth";
import { useGetDashboardQuery } from "../../redux/services/dashboard/dashboardApi";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import Dropdown from "../../shared/Dropdown";
import FeedbackCard from "./FeedbackCard";
import { SidebarContainer } from "../../components";
import { TotalsOverView } from "./TotalsOverView";
import { JobStatusReport } from "./JobStatusReport";
import { useConfig } from "../../hooks";
import { InvoiceReport } from "./InvoiceReport";
import LatestInvoices from "./LatestInvoices";
import { DateFilterDropdown } from "../../components";
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subMonths,
  subWeeks,
} from "date-fns";
import { buildOperationalUtcWindow } from "../../utils/date.utils";

const Dashboard = () => {
  const { user } = useAuth();
  const { appType } = useConfig();

  const formTypeOptions = [
    { value: "daily", label: "Todays" },
    { value: "weekly", label: "This Weeks" },
    { value: "monthly", label: "This Months" },
    { value: "yearly", label: "This Years" },
  ];
  const [viewType, setViewType] = useState(formTypeOptions[1]);
  const [dateFilter, setDateFilter] = useState({ type: "thisWeek" });

  // Build query parameters based on date filter
  const buildQueryParams = () => {
    const params = {};

    if (dateFilter.type === "all") {
      params.type = "all";
    } else {
      const range = resolveDateRangeForIndexLinks(dateFilter);
      if (!range) {
        return { type: "all" };
      }

      const normalizedRange = buildOperationalUtcWindow(range.start, range.end);
      params.type = "custom";
      params.startDate = normalizedRange.startDate;
      params.endDate = normalizedRange.endDate;
    }

    return params;
  };

  // Fetch dashboard data using RTK Query
  const {
    data: dashboard = {},
    isLoading,
    isFetching,
  } = useGetDashboardQuery(buildQueryParams());

  function onFormChange(e) {
    setViewType(e);
    // RTK Query automatically handles cache invalidation
  }

  function onDateFilterChange(filter) {
    setDateFilter(filter);
    // RTK Query automatically refetches with new parameters
  }

  function resolveDateRangeForIndexLinks(filter) {
    const today = new Date();
    const type = filter?.type;

    // Prefer the explicit range if the dropdown already provided it
    if (filter?.start && filter?.end) {
      const start = new Date(filter.start);
      const end = new Date(filter.end);
      if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
        return { start, end };
      }
    }

    switch (type) {
      case "today":
        return { start: startOfDay(today), end: endOfDay(today) };
      case "thisWeek":
        return { start: startOfWeek(today), end: endOfWeek(today) };
      case "lastWeek": {
        const last = subWeeks(today, 1);
        return { start: startOfWeek(last), end: endOfWeek(last) };
      }
      case "thisMonth":
        return { start: startOfMonth(today), end: endOfMonth(today) };
      case "lastMonth": {
        const last = subMonths(today, 1);
        return { start: startOfMonth(last), end: endOfMonth(last) };
      }
      case "thisYear":
        return { start: startOfYear(today), end: endOfYear(today) };
      case "singleDate":
      case "customRange":
        // If these are missing start/end, we can't reconstruct reliably.
        return null;
      case "all":
      default:
        return null;
    }
  }

  const dashboardIndexLinkQuery = (() => {
    const range = resolveDateRangeForIndexLinks(dateFilter);
    if (!range) return "";
    const normalizedRange = buildOperationalUtcWindow(range.start, range.end);
    if (!normalizedRange.startDate || !normalizedRange.endDate) return "";
    const params = new URLSearchParams({
      source: "dashboard",
      startDate: normalizedRange.startDate,
      endDate: normalizedRange.endDate,
    });
    return `?${params.toString()}`;
  })();

  const jobsFooterLink = `/jobs${dashboardIndexLinkQuery}`;
  const invoicesFooterLink = `/invoices${dashboardIndexLinkQuery}`;
  const dashboardChartRange = resolveDateRangeForIndexLinks(dateFilter);

  function renderLabel(str, type) {
    return `${type.label} ${str}`;
  }

  const formTypeTrigger = (
    <div className="flex items-center cursor-pointer font-semibold">
      {renderLabel("Data", viewType)}
      <ChevronDownIcon className="h-3 w-3 ml-1" />
    </div>
  );

  const dateFilterTrigger = (
    <div className="flex items-center cursor-pointer font-semibold">
      <span>
        {dateFilter.type
          ? `${
              dateFilter.type === "customRange"
                ? "Custom"
                : dateFilter.type === "singleDate"
                ? "Single Date"
                : dateFilter.type === "all"
                ? "All Time"
                : dateFilter.type === "today"
                ? "Today's"
                : dateFilter.type === "thisWeek"
                ? "This Week's"
                : dateFilter.type === "thisMonth"
                ? "This Month's"
                : dateFilter.type === "thisYear"
                ? "This Year's"
                : dateFilter.type === "lastMonth"
                ? "Last Month's"
                : dateFilter.type === "lastWeek"
                ? "Last Week's"
                : "This Week's"
            } Data`
          : "This Week's Data"}
      </span>
      <ChevronDownIcon className="h-3 w-3 ml-1" />
    </div>
  );

  const actions = (
    <div className="flex space-x-4 items-center">
      {/* <Dropdown
        align="left"
        closeOnClick
        trigger={formTypeTrigger}
        data={formTypeOptions}
        itemClick={onFormChange}
      /> */}

      <DateFilterDropdown
        align="left"
        trigger={dateFilterTrigger}
        onChange={onDateFilterChange}
        value={dateFilter}
      />
    </div>
  );

  const loading = isLoading;

  const feedbacks =
    user.authority === "superAdmin" &&
    dashboard.feedbacks &&
    dashboard.feedbacks?.map((i) => {
      return (
        <FeedbackCard
          key={i._id}
          i={i}
          link={i._id}
          title={i.type}
          body={i.messages}
          color="orange"
          loading={loading}
        />
      );
    });

  const headerRef = useRef();
  const [headerContext, setHeaderContext] = useState();
  useEffect(() => {
    setHeaderContext(headerRef.current);
  }, [headerRef.current]);

  const renderHeader = () => {
    return (
      <div ref={headerRef} className="p-2 border-b">
        {actions}
      </div>
    );
  };

  const isAdmin =
    user.authority === "admin" ||
    user.authority === "owner" ||
    user.authority === "superAdmin";

  // Get view type based on date filter
  const getViewType = () => {
    if (dateFilter.type === "thisWeek") return "weekly";
    if (dateFilter.type === "lastWeek") return "weekly";
    if (dateFilter.type === "thisMonth") return "monthly";
    if (dateFilter.type === "thisYear") return "yearly";
    if (dateFilter.type === "today") return "daily";
    if (dateFilter.type === "all") return "all";
    if (dateFilter.type === "lastMonth") return "monthly";
    return "weekly"; // Default
  };

  return (
    <SidebarContainer header={renderHeader} hideSidebar className="w-full">
      {({ parentHeight }) => {
        const offsetTop = headerContext?.offsetTop;
        const offsetHeight = headerContext?.offsetHeight;
        const wrapperStyle = {
          height: `${parentHeight - offsetTop - offsetHeight}px`,
        };

        return (
          <div
            style={wrapperStyle}
            className="px-3 pb-3  overflow-y-auto w-full "
          >
            <div className="my-3 space-y-2">
              {isAdmin && <TotalsOverView loading={loading} data={dashboard} />}
              {isAdmin && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <JobStatusReport
                    viewType={getViewType()}
                    appType={appType}
                    startDate={dashboardChartRange?.start}
                    endDate={dashboardChartRange?.end}
                  />
                  <InvoiceReport
                    viewType={getViewType()}
                    appType={appType}
                    startDate={dashboardChartRange?.start}
                    endDate={dashboardChartRange?.end}
                  />

                  <NewestJobs
                    title="Jobs"
                    status={isLoading ? "loading" : "succeeded"}
                    jobs={dashboard.jobs || []}
                    footerLink={jobsFooterLink}
                  />
                  <LatestInvoices
                    title="Invoices"
                    status={isLoading ? "loading" : "succeeded"}
                    invoices={dashboard.invoices || []}
                    footerLink={invoicesFooterLink}
                  />
                </div>
              )}
            </div>
          </div>
        );
      }}
    </SidebarContainer>
  );
};

export default Dashboard;
